import { BaseInput, Button, useColorModeValue } from '@/components';
import { PRIMARY_COLOR } from '@/constants';
import { useAuth } from '@/hooks';
import { trans } from '@/i18n';
import { ROUTE_PATH } from '@/routes';
import { AuthService } from '@/services';
import { logout } from '@/store/auth/admin';
import { showError, showSuccess } from '@/utils';
import { Flex, Heading, Text } from '@chakra-ui/react';
import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { ResetPasswordDTO, ResetPasswordFormDTO } from './dto/resetPasswordForm.dto';
import { resetPasswordYup } from './utils/resetPassword.validate';

function ResetPasswordPage() {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const textColor = useColorModeValue('#6A6A6A');

  const [loading, setLoading] = useState(false);

  const buttonStyles = {
    fontSize: 'sm',
    bg: PRIMARY_COLOR,
    w: '100%',
    h: '55px',
    color: 'white',
    _hover: { bg: 'teal.200' },
    _active: { bg: 'teal.400' },
    fontWeight: 'bold',
  };

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ResetPasswordFormDTO>({
    mode: 'onChange',
    resolver: resetPasswordYup,
  });

  const onSubmit: SubmitHandler<ResetPasswordFormDTO> = async (data) => {
    const sv = new AuthService();
    setLoading(true);
    const body: ResetPasswordDTO = {
      newPassword: data.password,
      token: auth?.accessToken || '',
    };

    try {
      const res = await sv.resetPassword(body);
      if (res?.status === 200) {
        showSuccess();
        logout();
        navigate(ROUTE_PATH.LOGIN);
      }
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Helmet>
        <title>{trans('resetPassword.pageTitle')}</title>
      </Helmet>

      <Flex minH="100vh" align="center" justify="center" backgroundColor="white">
        <Flex
          direction="column"
          gap="36px"
          bg="transparent"
          mx={{ base: 'auto', lg: 'unset' }}
          w={{ base: '100%', md: '405px' }}
          userSelect="none"
          p={{ base: '45px', md: '0' }}
        >
          <Flex direction="column" gap="8.5px">
            <Heading color={textColor} fontSize="32px" fontWeight="bold">
              {trans('resetPassword.pageTitle')}
            </Heading>

            <Text color={textColor} fontWeight="bold" fontSize="14px">
              {trans('resetPassword.subtitle')}
            </Text>
          </Flex>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex direction="column" gap="47px" px={{ base: '0', md: '26px' }}>
              <Flex direction="column" gap="19px">
                <BaseInput
                  className="reset-password-input"
                  errors={errors}
                  control={control}
                  name="password"
                  label={trans('resetPassword.password')}
                  placeholder={trans('resetPassword.yourPassword')}
                  isPassword
                />
                <BaseInput
                  className="reset-password-input"
                  errors={errors}
                  control={control}
                  name="rePassword"
                  label={trans('resetPassword.rePassword')}
                  placeholder={trans('resetPassword.yourPassword')}
                  isPassword
                />
              </Flex>

              <Button type="submit" loading={loading} {...buttonStyles}>
                {trans('resetPassword.submit')}
              </Button>
            </Flex>
          </form>
        </Flex>
      </Flex>
    </>
  );
}

export default ResetPasswordPage;
